// Session validation utility for Applauss authentication
import axios from 'axios';
import { getApplaussAuth, clearApplaussAuth, refreshApplaussToken } from './authManager';
import { getBackendBaseUrl } from './backendUrl';

const API_BASE_URL = getBackendBaseUrl();

// Prevent duplicate validations (React StrictMode executes effects twice in dev)
// Store the in-flight Promise so concurrent calls share the same request
let validationPromise = null;
let initialValidationComplete = false;

/**
 * Update Applauss auth data in localStorage with fresh data from server
 * @param {Object} validationData - Data from /auth/validate endpoint
 * @param {Object} validationData.user - Updated user information
 * @param {number} validationData.expires_at - Token expiration timestamp (seconds)
 */
export function updateApplaussAuthFromValidation(validationData) {
  const { user, expires_at } = validationData;

  if (!user || !expires_at) {
    console.warn('⚠️ Invalid validation data received');
    return;
  }

  // Update user info in localStorage
  const existingAuth = getApplaussAuth();
  if (existingAuth) {
    // Keep existing tokens, only update user info and expiry.
    // /auth/validate owns every field it returns, so a null display_name must overwrite
    // (it means "no name set") — do not add null-filtering, it would pin a stale name.
    // `picture` is absent rather than null, so the spread preserves it.
    const updatedUser = {
      ...existingAuth.user,
      ...user, // Merge with fresh user data from server
    };

    localStorage.setItem('applauss_user', JSON.stringify(updatedUser));

    // Update expiry timestamp (convert from seconds to milliseconds)
    const expiryMs = expires_at * 1000;
    localStorage.setItem('applauss_token_expiry', expiryMs.toString());

    console.log('✅ Auth data updated from validation:', {
      user_id: user.id,
      email: user.email,
      providers: user.providers,
      expires_at_ms: expiryMs
    });
  }
}

// Note: refreshApplaussToken() is now centralized in authManager.js
// This eliminates the race condition that occurred when both authValidator.js
// and backendApi.js had their own refresh implementations

/**
 * Check if the initial session validation has completed
 * This prevents race conditions where components try to use auth state
 * before the initial validation finishes
 * @returns {boolean} True if initial validation has completed
 */
export function isInitialValidationComplete() {
  return initialValidationComplete;
}

/**
 * Reset the initial validation flag (useful for testing)
 */
export function resetInitialValidationFlag() {
  initialValidationComplete = false;
  validationPromise = null;
}

/**
 * Validate current session with backend on app mount
 * Checks if the stored token is actually valid (not just unexpired)
 * Useful for detecting revoked tokens, session changes, etc.
 *
 * @returns {Promise<Object>} Validation result
 * @returns {boolean} result.valid - Whether the session is valid
 * @returns {Object} result.user - User data if valid
 * @returns {string} result.error - Error message if validation failed
 */
export function validateApplaussSession() {
  // If a validation is already in flight, return the same Promise
  // This correctly handles React StrictMode double-invocation
  if (validationPromise) {
    return validationPromise;
  }

  validationPromise = _doValidate().finally(() => {
    validationPromise = null;
  });

  return validationPromise;
}

async function _doValidate() {
  const auth = getApplaussAuth();

  // No token stored - nothing to validate
  if (!auth || !auth.access_token) {
    initialValidationComplete = true;
    return { valid: false, reason: 'no_token' };
  }

  // Token is expired - try to refresh it automatically
  if (auth.isExpired) {
    console.log('⏱️ Token expired, attempting automatic refresh...');

    try {
      const refreshResult = await refreshApplaussToken();

      if (refreshResult.success) {
        // Token refreshed successfully, return as valid session
        initialValidationComplete = true;
        return {
          valid: true,
          user: refreshResult.user,
          refreshed: true
        };
      }

      // Refresh failed or in progress
      initialValidationComplete = true;
      return { valid: false, reason: 'expired', refresh_failed: true };
    } catch {
      // Refresh failed - token is invalid
      console.warn('⚠️ Token refresh failed, session is invalid');
      initialValidationComplete = true;
      return { valid: false, reason: 'refresh_failed' };
    }
  }

  // Token exists and not expired - validate with server

  try {
    const response = await axios.get(`${API_BASE_URL}/auth/validate`, {
      headers: {
        'Authorization': `Bearer ${auth.access_token}`
      }
    });

    const { valid, user, expires_at, last_activity } = response.data;

    if (!valid) {
      // Token is invalid/revoked - clear auth
      console.warn('⚠️ Session validation failed: Token is invalid or revoked');
      clearApplaussAuth();
      return { valid: false, reason: 'invalid_token' };
    }

    // Valid token - update user info and expiry with fresh data
    updateApplaussAuthFromValidation({ user, expires_at });

    console.log('✅ Session validated successfully:', {
      user_id: user.id,
      email: user.email,
      last_activity: last_activity
    });

    const result = {
      valid: true,
      user,
      last_activity
    };

    initialValidationComplete = true;
    return result;

  } catch (error) {
    initialValidationComplete = true;

    // 401/403 = token invalid/expired
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('⚠️ Session validation failed: Unauthorized (401/403)');
      clearApplaussAuth();
      return { valid: false, reason: 'unauthorized' };
    }

    // 5xx server errors - treat as invalid to be safe
    if (error.response?.status >= 500) {
      console.warn('⚠️ Server error during validation (treating as invalid):', error.response.status);
      return {
        valid: false,
        reason: 'server_error',
        error: error.message
      };
    }

    // Network error (no response) - only assume valid if token is fresh
    // This allows offline scenarios but limits risk of using stale/revoked tokens
    if (!error.response) {
      const tokenAge = auth.expiry ? Date.now() - (auth.expiry - 3600000) : Infinity;
      const isTokenFresh = tokenAge < 300000; // Less than 5 minutes old

      if (isTokenFresh) {
        console.warn('⚠️ Network error during validation, token is fresh - assuming valid');
        return {
          valid: true,
          error: error.message,
          assumedValid: true
        };
      } else {
        console.warn('⚠️ Network error during validation, token is old - treating as invalid');
        return {
          valid: false,
          reason: 'network_error_stale_token',
          error: error.message
        };
      }
    }

    // Other errors - treat as invalid
    console.warn('⚠️ Unexpected error during validation:', error.message);
    return {
      valid: false,
      reason: 'validation_error',
      error: error.message
    };
  }
}
